import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Plus,
  Search,
  Edit2,
  Trash2,
  Sparkles,
  RefreshCw,
  FolderOpen,
  User,
  Clock,
  ExternalLink,
  Eye,
  AlertTriangle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import {
  useAuthorPostsQuery,
  useCategoriesQuery,
  useDeletePostMutation,
} from "../../hooks/queries/useBlogQueries";
import { BlogPost } from "../../types/blog";
import { useToast } from "../../hooks/otherHooks/useToast";

const SuperAdminBlog: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const { data: posts = [], isLoading: postsLoading, refetch } = useAuthorPostsQuery();
  const { data: categories = [] } = useCategoriesQuery();
  const deletePostMutation = useDeletePostMutation();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>("all");
  const [deletingPost, setDeletingPost] = useState<BlogPost | null>(null);

  // Filter posts
  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        q === "" ||
        post.title.toLowerCase().includes(q) ||
        (post.excerpt && post.excerpt.toLowerCase().includes(q)) ||
        (typeof post.author === "string" && post.author.toLowerCase().includes(q)) ||
        (post.author?.user &&
          `${post.author.user.first_name || ""} ${post.author.user.last_name || ""}`
            .toLowerCase()
            .includes(q));

      const matchesCategory =
        selectedCategoryFilter === "all" ||
        (post.category && String(post.category.id) === selectedCategoryFilter);

      return matchesSearch && matchesCategory;
    });
  }, [posts, searchQuery, selectedCategoryFilter]);

  // Handle Delete Confirmation
  const handleDeleteConfirm = () => {
    if (!deletingPost) return;
    deletePostMutation.mutate(deletingPost.id, {
      onSuccess: () => {
        addToast({
          message: `Article "${deletingPost.title}" deleted successfully.`,
          bg: "success",
        });
        setDeletingPost(null);
      },
      onError: (err: any) => {
        addToast({
          message: err.message || "Failed to delete article.",
          bg: "danger",
        });
      },
    });
  };

  // Format Date Helper
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="cht-space-y-6 cht-max-w-7xl cht-mx-auto cht-pb-12 cht-animate-in cht-fade-in cht-duration-700">
      {/* Banner Card */}
      <div className="cht-rounded-[32px] cht-bg-gradient-to-r cht-from-[#1e293b] cht-via-indigo-950 cht-to-[#1e293b] cht-p-6 sm:cht-p-8 cht-text-white cht-relative cht-overflow-hidden cht-shadow-xl cht-border cht-border-indigo-900/40">
        <div className="cht-absolute cht-right-0 cht-top-0 cht-w-96 cht-h-96 cht-bg-[#3e57da]/10 cht-rounded-full cht-blur-3xl cht-pointer-events-none" />
        <div className="cht-relative cht-z-10 cht-flex cht-flex-col md:cht-flex-row md:cht-items-center cht-justify-between cht-gap-6">
          <div className="cht-space-y-2 cht-max-w-xl">
            <div className="cht-inline-flex cht-items-center cht-space-x-2 cht-bg-white/10 cht-backdrop-blur-md cht-px-3.5 cht-py-1 cht-rounded-full cht-text-[11px] cht-font-black cht-text-indigo-300 cht-border cht-border-white/15">
              <Sparkles className="cht-h-3.5 cht-w-3.5 cht-text-indigo-300" />
              <span>Editorial & Publication Engine</span>
            </div>
            <h1 className="cht-text-2xl sm:cht-text-4xl cht-font-black cht-tracking-tight cht-text-white cht-leading-tight">
              Blog Articles & Content Control
            </h1>
            <p className="cht-text-xs sm:cht-text-sm cht-text-slate-300 cht-font-normal cht-leading-relaxed">
              Draft, compose, format, organize, and publish technical thought leadership, platform announcements, and engineering articles.
            </p>
          </div>

          <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-3 cht-shrink-0">
            <Button
              variant="primary"
              onClick={() => navigate("/superadmin/dashboard/blog/create")}
              leftIcon={<Plus className="cht-h-4 cht-w-4" />}
              className="cht-rounded-xl cht-font-black cht-text-xs"
            >
              Compose Article
            </Button>
            <Button
              variant="outline"
              onClick={() => refetch()}
              leftIcon={<RefreshCw className={`cht-h-4 cht-w-4 ${postsLoading ? "cht-animate-spin" : ""}`} />}
              className="cht-rounded-xl cht-font-bold cht-text-xs cht-bg-white/10 cht-text-white cht-border-white/20 hover:cht-bg-white/20"
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Stat Cards */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-3 cht-gap-4">
        <div className="cht-p-5 cht-rounded-[24px] cht-bg-white cht-border cht-border-gray-100 cht-shadow-sm cht-flex cht-items-center cht-justify-between">
          <div className="cht-space-y-1">
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">Total Articles</p>
            <p className="cht-text-2xl cht-font-black cht-text-[#1e293b]">{posts.length}</p>
          </div>
          <div className="cht-h-12 cht-w-12 cht-rounded-2xl cht-bg-indigo-50 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center cht-shrink-0">
            <FileText className="cht-h-6 cht-w-6" />
          </div>
        </div>

        <div className="cht-p-5 cht-rounded-[24px] cht-bg-white cht-border cht-border-gray-100 cht-shadow-sm cht-flex cht-items-center cht-justify-between">
          <div className="cht-space-y-1">
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">Categories</p>
            <p className="cht-text-2xl cht-font-black cht-text-[#1e293b]">{categories.length}</p>
          </div>
          <div className="cht-h-12 cht-w-12 cht-rounded-2xl cht-bg-emerald-50 cht-text-emerald-600 cht-flex cht-items-center cht-justify-center cht-shrink-0">
            <FolderOpen className="cht-h-6 cht-w-6" />
          </div>
        </div>

        <div className="cht-p-5 cht-rounded-[24px] cht-bg-white cht-border cht-border-gray-100 cht-shadow-sm cht-flex cht-items-center cht-justify-between">
          <div className="cht-space-y-1">
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">Published Articles</p>
            <p className="cht-text-2xl cht-font-black cht-text-[#1e293b]">{posts.length}</p>
          </div>
          <div className="cht-h-12 cht-w-12 cht-rounded-2xl cht-bg-purple-50 cht-text-purple-600 cht-flex cht-items-center cht-justify-center cht-shrink-0">
            <Eye className="cht-h-6 cht-w-6" />
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="cht-p-4 cht-bg-white cht-border cht-border-gray-100 cht-rounded-[24px] cht-shadow-sm cht-flex cht-flex-col sm:cht-flex-row cht-items-center cht-justify-between cht-gap-4">
        <div className="cht-w-full sm:cht-w-80 cht-relative">
          <Search className="cht-absolute cht-left-3.5 cht-top-1/2 -cht-translate-y-1/2 cht-h-4 cht-w-4 cht-text-gray-400" />
          <input
            type="text"
            placeholder="Search by title, excerpt, author..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="cht-w-full cht-h-11 cht-rounded-xl cht-bg-gray-50 cht-border cht-border-gray-200/80 cht-pl-10 cht-pr-4 cht-text-xs cht-font-bold cht-text-gray-800 cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 focus:cht-border-[#3e57da] cht-transition-all"
          />
        </div>

        <div className="cht-flex cht-items-center cht-space-x-3 cht-w-full sm:cht-w-auto">
          <select
            value={selectedCategoryFilter}
            onChange={(e) => setSelectedCategoryFilter(e.target.value)}
            className="cht-h-11 cht-rounded-xl cht-bg-gray-50 cht-border cht-border-gray-200/80 cht-px-3.5 cht-text-xs cht-font-bold cht-text-gray-700 cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 focus:cht-border-[#3e57da]"
          >
            <option value="all">All Categories</option>
            {categories.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Posts Table */}
      {postsLoading ? (
        <div className="cht-py-20 cht-text-center cht-space-y-3">
          <RefreshCw className="cht-h-8 cht-w-8 cht-text-[#3e57da] cht-animate-spin cht-mx-auto" />
          <p className="cht-text-xs cht-text-gray-500 cht-font-semibold">Loading articles...</p>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="cht-py-16 cht-px-6 cht-text-center cht-space-y-4 cht-bg-white cht-border cht-border-gray-100 cht-rounded-[28px] cht-shadow-sm">
          <FileText className="cht-h-12 cht-w-12 cht-text-gray-300 cht-mx-auto" />
          <div className="cht-space-y-1 cht-max-w-sm cht-mx-auto">
            <h3 className="cht-text-base cht-font-black cht-text-[#1e293b]">No Articles Found</h3>
            <p className="cht-text-xs cht-text-gray-500">
              {searchQuery
                ? `No articles match "${searchQuery}". Try clearing search.`
                : "No articles have been created yet. Click below to add your first publication."}
            </p>
          </div>
          <Button
            variant="primary"
            onClick={() => navigate("/superadmin/dashboard/blog/create")}
            leftIcon={<Plus className="cht-h-4 cht-w-4" />}
            className="cht-rounded-xl cht-font-bold cht-text-xs"
          >
            Compose Article
          </Button>
        </div>
      ) : (
        <div className="cht-bg-white cht-border cht-border-gray-100 cht-rounded-[28px] cht-overflow-hidden cht-shadow-sm">
          <div className="cht-overflow-x-auto">
            <table className="cht-w-full cht-text-left cht-text-xs">
              <thead className="cht-bg-gray-50/80 cht-border-b cht-border-gray-100 cht-text-gray-500 cht-font-black cht-uppercase cht-tracking-wider cht-text-[10px]">
                <tr>
                  <th className="cht-py-4 cht-px-6">Article & Details</th>
                  <th className="cht-py-4 cht-px-6">Category</th>
                  <th className="cht-py-4 cht-px-6">Published Date</th>
                  <th className="cht-py-4 cht-px-6 cht-text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="cht-divide-y cht-divide-gray-100 cht-font-medium cht-text-gray-800">
                {filteredPosts.map((post) => {
                  const authorName =
                    typeof post.author === "string"
                      ? post.author
                      : post.author?.user
                      ? `${post.author.user.first_name || ""} ${post.author.user.last_name || ""}`.trim()
                      : post.author?.first_name
                      ? `${post.author.first_name} ${post.author.last_name || ""}`.trim()
                      : "Editorial Staff";

                  const previewImg =
                    post.image ||
                    post.featured_image ||
                    post.featured_image_url ||
                    (post.featured_media ? post.featured_media.url : null) ||
                    "https://images.unsplash.com/photo-1498050108023-c5249f4df085?q=80&w=150&auto=format&fit=crop";

                  return (
                    <tr key={post.id} className="hover:cht-bg-gray-50/60 cht-transition-colors">
                      <td className="cht-py-4 cht-px-6">
                        <div className="cht-flex cht-items-start cht-gap-4 cht-max-w-lg">
                          <div className="cht-w-16 cht-h-12 cht-rounded-xl cht-overflow-hidden cht-border cht-border-gray-200/80 cht-shrink-0 cht-bg-gray-100">
                            <img
                              src={previewImg}
                              alt={post.title}
                              className="cht-w-full cht-h-full cht-object-cover"
                            />
                          </div>
                          <div className="cht-space-y-1 cht-min-w-0">
                            <p className="cht-font-black cht-text-[#1e293b] cht-text-sm cht-line-clamp-1">
                              {post.title}
                            </p>
                            {post.excerpt && (
                              <p className="cht-text-[11px] cht-text-gray-500 cht-line-clamp-1">
                                {post.excerpt}
                              </p>
                            )}
                            <div className="cht-flex cht-items-center cht-space-x-3 cht-text-[10px] cht-text-gray-400 cht-pt-0.5">
                              <span className="cht-flex cht-items-center cht-gap-1">
                                <User className="cht-h-3 cht-w-3" />
                                {authorName}
                              </span>
                              {(post.read_time || post.readTime) ? (
                                <span className="cht-flex cht-items-center cht-gap-1">
                                  <Clock className="cht-h-3 cht-w-3" />
                                  {post.read_time || post.readTime} min read
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="cht-py-4 cht-px-6">
                        {post.category ? (
                          <span className="cht-inline-flex cht-items-center cht-px-2.5 cht-py-1 cht-rounded-full cht-bg-indigo-50 cht-text-[#3e57da] cht-text-[10px] cht-font-black">
                            {post.category.name}
                          </span>
                        ) : (
                          <span className="cht-text-gray-400 cht-text-[11px]">Uncategorized</span>
                        )}
                      </td>
                      <td className="cht-py-4 cht-px-6 cht-text-gray-500 cht-whitespace-nowrap cht-font-semibold">
                        {formatDate(post.pub_date)}
                      </td>
                      <td className="cht-py-4 cht-px-6 cht-text-right cht-whitespace-nowrap">
                        <div className="cht-inline-flex cht-items-center cht-space-x-1.5">
                          {post.slug && (
                            <a
                              href={`/blog/${post.slug}`}
                              target="_blank"
                              rel="noreferrer"
                              className="cht-p-2 cht-text-gray-400 hover:cht-text-[#3e57da] cht-rounded-lg hover:cht-bg-indigo-50/60 cht-transition-colors"
                              title="View Public Post"
                            >
                              <ExternalLink className="cht-h-4 cht-w-4" />
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={() => navigate(`/superadmin/dashboard/blog/edit/${post.id}`)}
                            className="cht-p-2 cht-text-gray-500 hover:cht-text-[#3e57da] cht-rounded-lg hover:cht-bg-indigo-50/60 cht-transition-colors"
                            title="Edit Article"
                          >
                            <Edit2 className="cht-h-4 cht-w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeletingPost(post)}
                            className="cht-p-2 cht-text-gray-500 hover:cht-text-rose-600 cht-rounded-lg hover:cht-bg-rose-50/60 cht-transition-colors"
                            title="Delete Article"
                          >
                            <Trash2 className="cht-h-4 cht-w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      <AnimatePresence>
        {deletingPost && (
          <div className="cht-fixed cht-inset-0 cht-w-screen cht-h-screen cht-min-h-screen cht-bg-slate-900/60 cht-backdrop-blur-xs cht-z-[99999] cht-flex cht-items-center cht-justify-center cht-p-4 cht-overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="cht-bg-white cht-rounded-[24px] cht-p-6 cht-max-w-sm cht-w-full cht-shadow-2xl cht-space-y-4 cht-border cht-border-gray-100 cht-text-center cht-my-auto"
            >
              <div className="cht-h-14 cht-w-14 cht-rounded-2xl cht-bg-rose-50 cht-text-rose-600 cht-flex cht-items-center cht-justify-center cht-mx-auto cht-ring-8 cht-ring-rose-50/50">
                <AlertTriangle className="cht-h-7 cht-w-7" />
              </div>

              <div className="cht-space-y-1.5">
                <h3 className="cht-text-lg cht-font-black cht-text-[#1e293b]">Delete Article?</h3>
                <p className="cht-text-xs cht-text-gray-500 cht-leading-relaxed">
                  Are you sure you want to permanently delete article{" "}
                  <strong className="cht-text-[#1e293b] cht-font-black">"{deletingPost.title}"</strong>?
                  This action cannot be undone.
                </p>
              </div>

              <div className="cht-grid cht-grid-cols-2 cht-gap-3 cht-pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDeletingPost(null)}
                  className="cht-w-full cht-text-xs cht-font-bold cht-rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  onClick={handleDeleteConfirm}
                  isLoading={deletePostMutation.isPending}
                  disabled={deletePostMutation.isPending}
                  leftIcon={<Trash2 className="cht-h-3.5 cht-w-3.5" />}
                  className="cht-w-full cht-text-xs cht-font-bold cht-rounded-xl"
                >
                  Delete
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SuperAdminBlog;
